// stateful.ts
import { effect } from "@lit-labs/preact-signals";
import { AsyncDirective } from "lit/async-directive.js";
import { directive, PartType } from "lit/directive.js";
var componentHooks = {};
var currentComponent = null;
var hookIndex = 0;
var isAnythingRendering = false;
function hasDepsChanged(hooks, hookIndex2, deps) {
  const hook = hooks?.[hookIndex2];
  if (!hook)
    return true;
  return !hook || !!deps && (!hook.deps || deps.some((dep, i) => dep !== hook.deps[i]));
}
function setCurrentComponent(component, id) {
  currentComponent = { instance: component, id };
  hookIndex = 0;
}
function getCurrentComponent() {
  if (!currentComponent) {
    throw new Error("No current component context. Did you call setCurrentComponent?");
  }
  return currentComponent;
}
function useState(initialState, deps) {
  const component = getCurrentComponent();
  const currentHookIndex = hookIndex++;
  if (!componentHooks[component.id]) {
    componentHooks[component.id] = [];
  }
  const hooks = componentHooks[component.id];
  const setState = (newState) => {
    if (isAnythingRendering)
      throw Error(
        "You cannot update the state while the component is rendering.\nThis is probably because you are calling `useState` or `useEffect` from inside a render function.\nTry to move the state update to a place where it is not called during a render like a lifecycle method or a event handler."
      );
    const oldDeps = hooks[currentHookIndex]?.deps;
    hooks[currentHookIndex] = { value: newState, deps: oldDeps };
    component.instance.rerender();
  };
  if (hasDepsChanged(hooks, currentHookIndex, deps)) {
    hooks[currentHookIndex] = { value: initialState, deps };
  }
  return [hooks[currentHookIndex].value, setState];
}
function useEffect(useEffectCallback, deps) {
  const component = getCurrentComponent();
  const currentHookIndex = hookIndex++;
  if (!componentHooks[component.id]) {
    componentHooks[component.id] = [];
  }
  const hooks = componentHooks[component.id];
  if (hasDepsChanged(hooks, currentHookIndex, deps)) {
    hooks[currentHookIndex]?.cleanup?.();
    const cleanupNum = setTimeout(() => {
      const cleanup = useEffectCallback();
      hooks[currentHookIndex] = {
        cleanup,
        deps
      };
    });
    hooks[currentHookIndex] = {
      cleanup: () => clearTimeout(cleanupNum),
      deps
    };
  }
}
function _useMemo(computeValue, deps) {
  const currentComponent2 = getCurrentComponent();
  const currentHookIndex = hookIndex++;
  if (!componentHooks[currentComponent2.id]) {
    componentHooks[currentComponent2.id] = [];
  }
  const hooks = componentHooks[currentComponent2.id];
  if (!hooks)
    throw new Error("hooks of current component not found, should not happen");
  if (!hasDepsChanged(hooks, currentHookIndex, deps)) {
    return hooks[currentHookIndex];
  }
  hooks[currentHookIndex]?.cleanup?.();
  const hook = {
    deps
  };
  const [newValue, cleanup] = computeValue(hook) ?? [];
  hook.value = newValue;
  hook.cleanup = cleanup;
  hooks[currentHookIndex] = hook;
  return hooks[currentHookIndex];
}
function useMemo(computeValue, deps) {
  return _useMemo(computeValue, deps).value;
}
function useObservable(observable, deps) {
  if (!deps)
    throw new Error("useObservable requires deps");
  return _useMemo((hook) => {
    const component = getCurrentComponent();
    let immediateRender = true;
    const subscription = observable().subscribe((value) => {
      hook.value = value;
      if (!immediateRender)
        component.instance.rerender();
    });
    immediateRender = false;
    return [hook.value, () => subscription.unsubscribe()];
  }, deps).value;
}
var StatefulDirective = class extends AsyncDirective {
  constructor(partInfo) {
    super(partInfo);
    /** @deprecated internal */
    this.debounceTimer = null;
    if (partInfo.type !== PartType.CHILD) {
      throw new Error("This directive can only be used in child parts.");
    }
  }
  update(_part, props) {
    _part.parentNode.__STATE = props;
    const fn = props[0];
    const componentProps = props[1];
    const componentFunctionID = props[2];
    if (!partComponentMap.has(_part)) {
      partComponentMap.set(_part, /* @__PURE__ */ new Map());
    }
    const componentsInPart = partComponentMap.get(_part);
    if (!componentsInPart.has(fn)) {
      componentsInPart.set(fn, idCounter++);
    }
    const componentId = componentsInPart.get(fn);
    this.componentID = componentId;
    this.componentFunctionID = componentFunctionID;
    this.renderFn = fn;
    this.props = componentProps;
    this.part = _part;
    return this.render(fn, componentProps, componentId);
  }
  render(value, props, id) {
    return this.execute();
  }
  disconnected() {
    super.disconnected();
    componentHooks[this.componentID]?.forEach((hook) => hook.cleanup?.());
    delete componentHooks[this.componentID];
    if (!this.part)
      throw new Error("part missing, this cant happen");
    partComponentMap.delete(this.part);
    this.effectDispose?.();
  }
  reconnected() {
    super.reconnected();
    this.rerender();
  }
  /** @deprecated internal */
  execute() {
    this.effectDispose?.();
    let result;
    let isFirstRender = true;
    const runRender = () => {
      setCurrentComponent(this, this.componentID);
      isAnythingRendering = true;
      try {
        result = this.renderFn(this.props);
        this.setValue(result);
        isFirstRender = false;
      } catch (e) {
        console.error(e);
        result = e instanceof Error ? e.message : e;
      }
      isAnythingRendering = false;
    };
    const renderWithDebounce = () => {
      if (isFirstRender) {
        runRender();
      } else {
        if (this.debounceTimer !== null)
          clearTimeout(this.debounceTimer);
        this.debounceTimer = window.setTimeout(() => {
          this.effectDispose?.();
          if (!this.isConnected)
            return;
          this.effectDispose = effect(() => runRender());
          this.debounceTimer = null;
        }, 0);
      }
    };
    this.effectDispose = effect(renderWithDebounce);
    return result;
  }
  // * here we accept outside rerender requests (mostly from hooks through useState, useObservable, etc)
  rerender() {
    this.execute();
  }
};
var statefulDirective = directive(StatefulDirective);
var idCounter = 0;
var partComponentMap = /* @__PURE__ */ new Map();
var stateful = (fn) => {
  const componentFunctionID = idCounter++;
  return (props) => {
    if (idCounter === Number.MAX_SAFE_INTEGER) {
      alert(
        "Oh Almighty User, in our mortal folly, we have reached the limits of our numerical comprehension. We humbly beseech You to grant us a divine refresh, that we may continue our digital endeavors."
      );
      throw new Error("Too many stateful directives");
    }
    return statefulDirective(fn, props, componentFunctionID);
  };
};
export {
  _useMemo,
  getCurrentComponent,
  setCurrentComponent,
  stateful,
  useEffect,
  useMemo,
  useObservable,
  useState
};
